import { courseRuntimeUrl } from './courseRuntimeContext';
import api from '../httpClient';
import { submitRoleplayStream } from './roleplayStream';

export type RoleplayMode = 'text' | 'voice';
export type RoleplayPerformanceError = 'connection_failed' | 'transcription_timeout' | 'turn_failed'
  | 'playback_failed' | 'interrupted' | 'unknown';
export type RoleplayPerformanceMetrics = Partial<Record<
  'speech_stop_to_audio_ms' | 'submit_to_audio_ms' | 'text_first_visible_ms' | 'controls_ready_ms'
  | 'backend_decision_ms' | 'transcription_finalization_ms' | 'text_to_audio_ms'
  | 'directive_to_audio_ms' | 'playback_ms' | 'microphone_handoff_ms'
  | 'local_speech_stop_to_provider_ms' | 'local_speech_stop_to_audio_ms'
  | 'elevenlabs_connection_ms' | 'elevenlabs_text_buffer_ms' | 'elevenlabs_first_audio_ms'
  | 'elevenlabs_audio_to_playback_ms' | 'elevenlabs_max_audio_gap_ms', number>>;
export type RoleplayPerformanceNetwork = {
  sample_scope: 'openai_webrtc';
  rtt_ms?: number;
  jitter_ms?: number;
  /** Loss over the sampled interval; omitted without a comparable baseline. */
  packet_loss_percent?: number;
};
export type RoleplayPerformancePatch = {
  learner_turn_id?: number | null;
  generation?: number | null;
  metrics?: RoleplayPerformanceMetrics;
  network?: RoleplayPerformanceNetwork;
};
export type RoleplayPerformancePayload = RoleplayPerformancePatch & {
  record_id: string;
  sequence: number;
  mode: RoleplayMode;
  input_mode: RoleplayMode;
  status: 'started' | 'ok' | 'error' | 'cancelled';
  error_code?: RoleplayPerformanceError;
  client_release?: string;
};
export type RoleplayVoiceAlias = string;
export type RoleplayExpression =
  | 'at_ease'
  | 'guarded'
  | 'anxious'
  | 'sad'
  | 'overwhelmed'
  | 'ashamed'
  | 'defensive'
  | 'angry'
  | 'numb'
  | 'vulnerable'
  | 'relieved'
  | 'hopeful';

export type RoleplayPortraitConfig = {
  version: 2;
  asset_key: string;
};

export type RoleplayPersona = {
  key: string;
  name: string;
  portrait_kind: 'library_image' | 'static_media';
  portrait_config: RoleplayPortraitConfig | Record<string, never>;
  image_url: string | null;
};
export type RoleplaySessionStatus =
  | 'active'
  | 'evaluation_paused'
  | 'closing'
  | 'safety_paused'
  | 'crisis_paused'
  | 'completed'
  | 'ended_early';

export type RoleplayScenario = {
  question_id: number;
  key: string;
  version: string;
  title: string;
  subtitle: string;
  situation: string;
  learner_role: string;
  learning_objectives: string[];
  scorecard_categories?: RoleplayScorecardCategoryDefinition[];
  persona: RoleplayPersona & {
    starting_expression: RoleplayExpression;
    voice: RoleplayVoiceAlias;
  };
  max_active_seconds: number;
  max_learner_turns: number;
};

export type RoleplayTurn = {
  id: number;
  sequence: number;
  speaker: 'learner' | 'persona';
  text: string;
  input_mode: 'text' | 'voice' | 'system';
  response_purpose: 'opening' | 'reply' | 'closing' | null;
  reply_to_id: number | null;
  persona_expression: RoleplayExpression | null;
  metadata: Record<string, unknown>;
  created_at: string;
};

export type RoleplayActorDirective = {
  /** Speculative output must remain inaudible until the final session authorizes it. */
  provisional?: boolean;
  generation: number;
  purpose: 'opening' | 'reply' | 'closing';
  reply_to_turn_id: number | null;
  fixed_text: string;
  fixed_expression: RoleplayExpression | null;
  requires_expression: boolean;
  should_create_response: boolean;
  disconnect_after_response: boolean;
};

export type RoleplayOpeningPreparation = {
  token: string;
  text: string;
  expression: RoleplayExpression;
  expires_in_seconds: number;
};

/** A staff-authored skill the post-practice scorecard rates from 0 to 100. */
export type RoleplayScorecardCategoryDefinition = {
  key: string;
  label: string;
  description: string;
  /** Lucide icon name from the backend allowlist. */
  icon: string;
};

export type RoleplayScorecardTurnMark = 'hit' | 'miss' | 'none';

/** Stored turn IDs; the view reads quotes from the transcript, not the model. */
export type RoleplayScorecardMoment = {
  learner_turn: number;
  learner_turn_id: number;
  persona_turn_id: number | null;
};

export type RoleplayScorecardCategory = RoleplayScorecardCategoryDefinition & {
  score: number;
  last_score: number | null;
  summary: string;
  worked: string;
  next: string;
  moment: RoleplayScorecardMoment;
  turn_marks: Array<{ learner_turn: number; mark: RoleplayScorecardTurnMark }>;
};

export type RoleplayScorecard = {
  version: 1;
  headline: string;
  overall_score: number;
  last_overall_score: number | null;
  categories: RoleplayScorecardCategory[];
  turning_point: {
    category_key: string;
    text: string;
    moment: RoleplayScorecardMoment;
  } | null;
};

export type RoleplayDebrief = {
  summary?: string;
  strengths?: string[];
  evidence?: string[];
  next_steps?: string[];
  scorecard?: RoleplayScorecard;
};

export type RoleplayWinEvaluation = {
  outcome?: 'continue' | 'won';
  rationale?: string;
  evidence?: string[];
  learner_hint?: string;
  provider_response_id?: string;
  evaluated_at?: string;
};

export type RoleplayLearnerClock = {
  version: 1;
  elapsed_ms: number;
  observed_elapsed_ms: number;
  /** Learner time left at `server_now`; present when the attempt has a time limit. */
  remaining_ms?: number;
  running_since: string | null;
  server_now: string;
  epoch: string;
  runtime_id: string;
  wait_kind: string;
  input_event_id: string;
  expected_actor_generation: number | null;
  terminal: boolean;
  last_sequence: number;
};

/** Server verdict on one runtime action; `null` from the controller means it was never acknowledged. */
export type RoleplayRuntimeActionOutcome = { accepted: boolean; reason: string };

export type RoleplayRuntimeReady = {
  reason: 'actor_drained' | 'transport_ready' | 'resumed_saved_state' | 'input_recovery';
  actorGeneration?: number;
  inputEventId?: string;
};

export type RoleplayRuntimeAction = {
  action: 'claim' | 'input_finalizing' | 'ready' | 'suspend';
  runtime_id: string;
  event_id: string;
  sequence: number;
  epoch: string;
  learner_clock_version: 1;
  input_event_id?: string;
  actor_generation?: number;
  ready_reason?: RoleplayRuntimeReady['reason'];
};

export type RoleplaySession = {
  turn_processing?: boolean;
  safety_checks_pending?: number;
  learner_clock?: RoleplayLearnerClock | null;
  runtime_action?: RoleplayRuntimeActionOutcome;
  id: number;
  flow_run_id: number;
  /** The completed flow response the server recorded for a confirmed win. */
  user_response_id?: number | null;
  status: RoleplaySessionStatus;
  mode: RoleplayMode;
  question_updated_at: string;
  scenario: RoleplayScenario;
  turn_count: number;
  current_expression: RoleplayExpression;
  seconds_left: number;
  turns_left: number;
  turns: RoleplayTurn[];
  actor_directive: RoleplayActorDirective | null;
  ai_safety: Record<string, unknown>;
  ui_mode: 'normal' | 'supportive_distress' | 'clarify' | 'crisis';
  reflection: string;
  debrief: RoleplayDebrief;
  /** Another request is generating this attempt's debrief; poll until it lands. */
  debrief_pending?: boolean;
  win_evaluation: RoleplayWinEvaluation;
  completion_reason: string;
  safety_metadata: Record<string, unknown>;
  can_retry_evaluation: boolean;
  actor_error: string;
  started_at: string;
  ended_at: string | null;
};

export type RoleplayTurnProgress = {
  type: 'provisional' | 'preview' | 'session';
  session: RoleplaySession;
};

export type RoleplayTurnStreamOptions = {
  onProgress: (event: RoleplayTurnProgress) => void;
  signal?: AbortSignal;
};

export type RoleplaySessionBootstrap = {
  scenario: RoleplayScenario;
  session: RoleplaySession | null;
  /** Opt-in: newest 100 attempts for the selected run/question, in start order. */
  session_history?: RoleplaySession[];
};

export type RoleplayRealtimeConfig = {
  session?: RoleplaySession;
  learner_clock?: RoleplayLearnerClock | null;
  client_secret: string;
  client_secret_expires_at: number | null;
  realtime_model: string;
  transcription_model: string;
  rtc_endpoint: string;
  voice: RoleplayVoiceAlias;
  voice_provider?: 'elevenlabs';
  elevenlabs?: {
    single_use_token: string;
    token_expires_at: number;
    voice_id: string;
    model_id: string;
    output_format: 'pcm_24000';
  };
  actor_directive: RoleplayActorDirective | null;
  expression_contract: {
    tool: {
      type: 'function';
      name: 'set_roleplay_expression';
      description: string;
      parameters: {
        type: 'object';
        additionalProperties: false;
        properties: { expression: { type: 'string'; enum: RoleplayExpression[] } };
        required: ['expression'];
      };
    };
    values: Array<{ value: RoleplayExpression; label: string }>;
  };
};

const basePath = (flowName: string) =>
  `/api/flows/${encodeURIComponent(flowName)}/roleplay/session`;

// A flow may hold several roleplay questions. The id is only sent when the caller
// targets a specific one, so single-roleplay flows keep their existing requests.
const questionSelector = (questionId?: number | null) =>
  (typeof questionId === 'number' ? { question_id: questionId } : {});

export const roleplay = {
  runtimeAction: (flowName: string, sessionId: number, payload: RoleplayRuntimeAction, options?: { signal?: AbortSignal }) =>
    api.post<RoleplaySession>(courseRuntimeUrl(`${basePath(flowName)}/${sessionId}/runtime/`), payload, { timeout: 5000, ...options }),
  recordPerformance: (flowName: string, sessionId: number, payload: RoleplayPerformancePayload) =>
    api.post<void>(courseRuntimeUrl(`${basePath(flowName)}/${sessionId}/performance/`), payload, { timeout: 5000 }),
  getSession: (
    flowName: string,
    flowRunId: number,
    questionId?: number | null,
    options?: { includeHistory?: boolean; signal?: AbortSignal; timeout?: number },
  ) => {
    const { includeHistory, ...requestOptions } = options ?? {};
    return api.get<RoleplaySessionBootstrap>(courseRuntimeUrl(`${basePath(flowName)}/`), {
      params: {
        flow_run_id: flowRunId,
        ...questionSelector(questionId),
        ...(includeHistory ? { include_history: 1 } : {}),
      },
      ...requestOptions,
    });
  },
  prepareOpening: (flowName: string, flowRunId: number, questionId?: number | null) =>
    api.post<RoleplayOpeningPreparation>(
      courseRuntimeUrl(`/api/flows/${encodeURIComponent(flowName)}/roleplay/opening/prepare/`),
      { flow_run_id: flowRunId, ...questionSelector(questionId) },
    ),
  startSession: (
    flowName: string,
    flowRunId: number,
    mode: RoleplayMode,
    preparedOpeningToken?: string,
    questionId?: number | null,
  ) => api.post<RoleplaySession>(courseRuntimeUrl(`${basePath(flowName)}/`), {
    flow_run_id: flowRunId,
    mode,
    learner_clock_version: 1,
    ...(preparedOpeningToken ? { prepared_opening_token: preparedOpeningToken } : {}),
    ...questionSelector(questionId),
  }),
  submitTurn: (
    flowName: string,
    sessionId: number,
    payload: {
      text: string;
      input_mode: RoleplayMode;
      actor_output_mode?: RoleplayMode;
      client_event_id: string;
    },
    options?: RoleplayTurnStreamOptions,
  ) => options
    ? submitRoleplayStream(courseRuntimeUrl(`${basePath(flowName)}/${sessionId}/turns/stream/`), payload, options)
    : api.post<RoleplaySession>(courseRuntimeUrl(`${basePath(flowName)}/${sessionId}/turns/`), payload),
  persistActorTurn: (
    flowName: string,
    sessionId: number,
    payload: {
      text: string;
      generation: number;
      client_event_id: string;
      provider_response_id?: string;
      persona_expression?: RoleplayExpression;
      usage?: Record<string, unknown>;
    },
  ) => api.post<RoleplaySession>(courseRuntimeUrl(`${basePath(flowName)}/${sessionId}/actor-turns/`), payload),
  retryEvaluation: (flowName: string, sessionId: number) =>
    api.post<RoleplaySession>(courseRuntimeUrl(`${basePath(flowName)}/${sessionId}/evaluation/retry/`), {}),
  retryActor: (flowName: string, sessionId: number, mode: 'text' = 'text') =>
    api.post<RoleplaySession>(courseRuntimeUrl(`${basePath(flowName)}/${sessionId}/actor/retry/`), { mode }),
  endSession: (flowName: string, sessionId: number, reason = 'learner_ended') =>
    api.post<RoleplaySession>(courseRuntimeUrl(`${basePath(flowName)}/${sessionId}/end/`), { reason }),
  debrief: (flowName: string, sessionId: number, reflection: string) =>
    api.post<RoleplaySession>(courseRuntimeUrl(`${basePath(flowName)}/${sessionId}/debrief/`), { reflection }),
  resolveSafety: (flowName: string, sessionId: number, scope: 'persona' | 'self') =>
    api.post<RoleplaySession>(courseRuntimeUrl(`${basePath(flowName)}/${sessionId}/safety/`), { scope }),
  getRealtimeConfig: (flowName: string, sessionId: number) =>
    api.post<RoleplayRealtimeConfig>(courseRuntimeUrl(`${basePath(flowName)}/${sessionId}/realtime/`), { learner_clock_version: 1 }),
};
